import React, { useState } from 'react'
import { HomeStackParamList } from 'navigation/home-stack'
import { StackScreenProps } from '@react-navigation/stack'
import { PlaceDetailTopNavigationBar } from 'ui/common/top-navigation-bar'
import { ColumnLayout, RowLayout } from 'ui/common/layout'
import { colors } from 'infra/color'
import { rgba } from 'polished'
import { Body2 } from 'ui/common/text'
import { Linking, TouchableOpacity, View } from 'react-native'
import { WebView } from 'react-native-webview'
import { ProviderType } from 'infra/type'
import { Loading } from 'ui/common/loading'
import { useSafeAreaInsets } from 'infra/util'
import SendIntentAndroid from 'react-native-send-intent'
import { usePlace } from 'api/hooks'

export const PlaceDetailScreen: React.FC<
  StackScreenProps<HomeStackParamList, 'PlaceDetailScreen'>
> = ({ route }) => {
  const safeAreaInsets = useSafeAreaInsets()
  const placeId = route.params.place.id
  const { data: fetchedPlace } = usePlace(placeId)
  const place = fetchedPlace || route.params.place

  const providerType = route.params.provider
  const [selectedProvider, setSelectedProvider] =
    useState<ProviderType>(providerType)
  const currentReference = place.references?.find(
    (x) => x.provider === selectedProvider,
  )
  return (
    <ColumnLayout
      style={{
        flex: 1,
        backgroundColor: colors.gray.v100,
        paddingTop: safeAreaInsets.top,
      }}
    >
      <PlaceDetailTopNavigationBar place={place} />
      <RowLayout>
        <TabMenuItem
          title='네이버'
          isSelected={selectedProvider === 'naver'}
          onPress={() => setSelectedProvider('naver')}
        />
        <TabMenuItem
          title='카카오'
          isSelected={selectedProvider === 'kakao'}
          onPress={() => setSelectedProvider('kakao')}
        />
        <TabMenuItem
          title='구글'
          isSelected={selectedProvider === 'google'}
          onPress={() => setSelectedProvider('google')}
        />
      </RowLayout>
      <ColumnLayout style={{ flex: 1 }}>
        {currentReference?.link ? (
          <WebView
            source={{ uri: currentReference.link }}
            // NOTE(gogo): 주의! universal deep link 위한 custom app scheme 등도 마찬가지로 여기에 등록을 해줘야
            // onShouldStartLoadWithRequest 에서 처리를 해준다, 아니면 warn log 만 남고 조용히 실패해버림
            originWhitelist={['http', 'https', 'intent', 'kakaomap']}
            onShouldStartLoadWithRequest={(event) => {
              console.log(event.url)
              // do nothing
              if (
                [
                  'about:blank',
                  'https://t1.daumcdn.net/kas/static/safeframe.html',
                ].includes(event.url)
              ) {
                return false
              }
              // 네이버맵 브라우저로 조회
              if (event.url.startsWith('https://m.place.naver.com')) {
                return true
              }
              // 카카오맵 브라우저로 조회
              if (event.url.startsWith('https://place.map.kakao.com')) {
                return true
              }
              // 구글맵 브라우저로 조회
              if (event.url.startsWith('https://www.google.com/maps/place')) {
                return true
              }
              // 카카오맵 => "kakaomap 앱 열기" 버튼
              // intent 처리가 제대로 안 돼서 따로 처리
              if (event.url.startsWith('intent://place?id=')) {
                const idMatch = event.url.match(/id=([0-9]+)/)
                if (idMatch && idMatch[1]) {
                  const id = idMatch[1]
                  Linking.openURL(
                    `https://m.map.kakao.com/actions/detailMapView?id=${id}&refService=place`,
                  )
                  return false
                } else {
                  console.log('kakao map place id not found')
                  return true
                }
              }
              // `intent:kakaolink://` 이런 형태로 된 애들이 있음
              if (
                event.url.startsWith('intent:') &&
                !event.url.startsWith('intent://')
              ) {
                Linking.openURL(event.url.substring(7))
                return false
              }
              // 일반적인 처리
              Linking.openURL(event.url).catch(() => {
                // intent 열려고 했는데 실패한 경우, 마지막 try
                // 사실 android intent 는 얘로 처리하는 게 가장 안정적일 것 같긴 하다
                if (event.url.startsWith('intent://')) {
                  SendIntentAndroid.openChromeIntent(event.url)
                }
              })
              return false
            }}
            startInLoadingState
            allowsInlineMediaPlayback
            renderLoading={() => (
              <View
                style={{
                  position: 'absolute',
                  height: '100%',
                  width: '100%',
                }}
              >
                <Loading />
              </View>
            )}
          />
        ) : (
          <ColumnLayout
            style={{ flex: 1, alignItems: 'center', justifyContent: 'center' }}
          >
            <Body2>아직 등록되지 않았어요 😅</Body2>
          </ColumnLayout>
        )}
      </ColumnLayout>
    </ColumnLayout>
  )
}

const TabMenuItem: React.FC<{
  title: string
  onPress: () => void
  isSelected: boolean
}> = ({ title, onPress, isSelected }) => {
  return (
    <TouchableOpacity
      style={{
        flex: 1,
        alignItems: 'center',
        backgroundColor: isSelected ? rgba(colors.primary, 0.2) : colors.white,
        borderTopLeftRadius: 16,
        borderTopRightRadius: 16,
      }}
      onPress={onPress}
    >
      <Body2
        style={{
          fontWeight: 'bold',
          color: isSelected ? colors.primary : rgba(colors.gray.v800, 0.3),
          marginTop: 12,
          marginBottom: 8,
        }}
      >
        {title}
      </Body2>
      <View
        style={{
          width: '100%',
          height: 2,
          backgroundColor: isSelected ? colors.primary : colors.white,
        }}
      />
    </TouchableOpacity>
  )
}
